<template>
  <ag-index-page
    :view="view"
    :columns="columns"
    create-to="/admin/users/new"
    create-label="Crear usuario"
    create-icon="mdi-account-plus"
    remove-title="Eliminar usuario"
  />
</template>

<script setup lang="ts">
import type { Person, UserRole } from "bancojs";
import { banco } from "@/api/client";
import { useIndexView } from "@/composables/useIndexView";
import AgIndexPage from "@/components/AgIndexPage.vue";
import type { AgDataTableOptions } from "@/components/table/types";
import { roleLabel, stateLabel } from "@/utils/labels";

/**
 * Filtros que el controlador acepta de verdad.
 *
 * La versión anterior ofrecía "RUT" -> `dni`, que AdminUserController NO soporta
 * como @RequestParam: se enviaba y la API lo ignoraba, así que ese filtro nunca
 * funcionó. En su lugar se exponen `firstname` y `lastname`, que sí están.
 */
const view = useIndexView<Person, "id" | "username" | "name" | "email" | "firstname" | "lastname">({
  title: "Índice de usuarios",
  rowKey: "id",
  fetchPage: (params) => banco.admin.users.find(params),
  remove: (item) => banco.admin.users.destroy(item.id),
  removedMessage: (item) => `Se eliminó al usuario ID ${item.id}.`,
  filters: [
    { field: "id", label: "ID" },
    { field: "username", label: "Usuario" },
    { field: "name", label: "Nombre" },
    { field: "email", label: "Email" },
    { field: "firstname", label: "Nombres" },
    { field: "lastname", label: "Apellidos" },
  ],
  confirm: {
    expected: (item) => String(item.id),
    label: "ID Usuario",
    hint: "Para confirmar ingresa el <b>ID</b> del usuario a eliminar.",
    error: "El ID de usuario no es correcto.",
    warning: (item) =>
      `Esta acción eliminará al usuario ${item.id}, junto con todas sus llaves y certificados.`,
  },
});

const columns: AgDataTableOptions[] = [
  { header: { text: "#", value: "$index", width: 60, sortable: false }, row: { type: "INDEX" } },
  {
    header: { text: "ID", value: "id", class: "text-no-wrap" },
    // Ruta ABSOLUTA: Vue Router 4+ eliminó `append`.
    row: { type: "LINK", to: "/admin/users/{id}/edit" },
  },
  { header: { text: "Usuario", value: "username", class: "text-no-wrap" }, row: { type: "TEXT" } },
  { header: { text: "Nombre", value: "name", class: "text-no-wrap" }, row: { type: "TEXT" } },
  { header: { text: "Email", value: "email", class: "text-no-wrap" }, row: { type: "TEXT" } },
  {
    header: { text: "Estado", value: "state", class: "text-no-wrap" },
    row: {
      type: "CHIP",
      label: true,
      color: { ACTIVE: "success", INACTIVE: "error" },
      transform: (val) => stateLabel(val),
    },
  },
  {
    header: { text: "Roles", value: "roles", class: "text-no-wrap", sortable: false },
    row: {
      type: "CHIP",
      label: true,
      color: {},
      transform: (roles) => String((roles as UserRole[])?.length ?? 0),
      tooltip: true,
      tooltipContent: (roles) =>
        ((roles as UserRole[]) ?? []).map((r) => `<div>${roleLabel(r)}</div>`).join(""),
    },
  },
  {
    header: { text: "", value: "$options", class: "text-no-wrap", sortable: false },
    row: {
      type: "OPTIONS",
      options: [
        { label: "Editar", icon: "mdi-pencil", link: "/admin/users/{id}/edit" },
        {
          label: "Eliminar",
          icon: "mdi-delete",
          action: (item) => view.openRemove(item as Person),
        },
      ],
    },
  },
];
</script>
